import React, { useState, useCallback } from 'react';
import { 
  BsChatLeft, 
  BsGear,
  BsTrash,
  BsChevronLeft,
  BsChevronRight,
  BsChevronDown,
  BsChevronUp,
  BsPlusLg
} from 'react-icons/bs';
import ThemeToggle from '../ThemeToggle';
import './Sidebar.css';

const Sidebar = ({ 
  conversations, 
  onSelect, 
  onDelete, 
  onNew, 
  models, 
  selectedModel, 
  setSelectedModel, 
  temperature, 
  setTemperature, 
  maxTokens, 
  setMaxTokens,
  currentConversation,
  isStreamingEnabled,
  setIsStreamingEnabled,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showChats, setShowChats] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleToggleChats = useCallback(() => {
    setShowChats(prev => !prev);
  }, []);

  const handleToggleSettings = useCallback(() => {
    setShowSettings(prev => !prev);
  }, []);
  
  const handleSelectConversation = useCallback((conv) => {
    if (onSelect) {
      onSelect(conv);
    }
  }, [onSelect]);

  const handleDeleteConversation = useCallback((convId, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      onDelete(convId);
    }
  }, [onDelete]);

  const handleSelectModel = useCallback((event) => {
    const value = event.target.value;
    setSelectedModel(value);
  }, [setSelectedModel]);

  const handleTemperatureChange = useCallback((event) => {
    const value = parseFloat(event.target.value);
    setTemperature(value);
  }, [setTemperature]);

  const handleMaxTokensChange = useCallback((event) => {
    const value = event.target.value ? parseInt(event.target.value) : null;
    setMaxTokens(value);
  }, [setMaxTokens]);

  const renderConversationItem = useCallback((conv) => (
    <div 
      key={conv.id} 
      className={`conversation-item ${currentConversation?.id === conv.id ? 'active' : ''}`}
    >
      <div 
        className="conversation-info" 
        onClick={() => handleSelectConversation(conv)}
      >
        <BsChatLeft className="conversation-icon" />
        <span className="conversation-title">
          {conv.title || `Chat ${conversations.indexOf(conv) + 1}`}
        </span>
      </div>
      <button
        type="button"
        className="delete-btn"
        onClick={() => handleDeleteConversation(conv.id)}
        title="Delete conversation"
      >
        <BsTrash />
      </button>
    </div>
  ), [currentConversation, conversations, handleDeleteConversation, handleSelectConversation]);

  return (
    <div className={`sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="sidebar-header">
        {isExpanded && <h3>NuraAI</h3>}
        <button 
          type="button"
          className="sidebar-toggle"
          onClick={handleToggleExpand}
          title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          {isExpanded ? <BsChevronLeft /> : <BsChevronRight />}
        </button>
      </div>

      <div className="sidebar-content">
        {isExpanded && (
          <>
            <div className="scrollable-content">
              <button 
                type="button"
                onClick={() => onNew()}
                className="new-chat-btn"
              >
                <BsPlusLg />
                <span>New Chat</span>
              </button>

              <div className="section-header" onClick={handleToggleChats}>
                <div className="section-title">
                  <BsChatLeft />
                  <span>Chats</span>
                </div>
                {showChats ? <BsChevronUp /> : <BsChevronDown />}
              </div>
              
              {showChats && (
                <div className="chats-section">
                  {Array.isArray(conversations) && conversations.map(renderConversationItem)}
                </div>
              )}
            </div>

            <div className="settings-wrapper">
              <div 
                className="section-header" 
                onClick={handleToggleSettings}
              >
                <div className="section-title">
                  <BsGear />
                  <span>Settings</span>
                </div>
                {showSettings ? <BsChevronUp /> : <BsChevronDown />}
              </div>

              {showSettings && (
                <div className="settings-section">
                  <div className="setting-item">
                    <label htmlFor="model-select">Model:</label>
                    <select 
                      id="model-select"
                      value={selectedModel || ''} 
                      onChange={handleSelectModel}
                    >
                      {Object.entries(models || {}).map(([key, model]) => (
                        <option key={key} value={key}>
                          {model.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="setting-item">
                    <label htmlFor="temperature-input">
                      Temperature: {temperature}
                    </label>
                    <input
                      id="temperature-input"
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={temperature}
                      onChange={handleTemperatureChange}
                    />
                  </div>
                  <div className="setting-item">
                    <label htmlFor="max-tokens-input">Max Tokens:</label>
                    <input
                      id="max-tokens-input"
                      type="number"
                      value={maxTokens || ''}
                      onChange={handleMaxTokensChange}
                      placeholder="Auto"
                    />
                  </div>
                  <div className="setting-item">
                    <label>Theme</label>
                    <ThemeToggle />
                  </div>
                  <div className="setting-item">
                    <label htmlFor="streaming-toggle">
                      Streaming Responses
                    </label>
                    <input
                      id="streaming-toggle"
                      type="checkbox"
                      checked={isStreamingEnabled}
                      onChange={(e) => setIsStreamingEnabled(e.target.checked)}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;